/**
 * PartyKycDocsSection — shared KYC document upload component.
 *
 * Owns:
 *   GET  /veda-agro/party-kyc/:partyType/:partyId/documents  (on mount + after mutation)
 *   POST /veda-agro/party-kyc/:partyType/:partyId/documents  (FormData: file + docType)
 *   DELETE /veda-agro/party-kyc/:partyType/:partyId/documents/:docId
 *
 * Used by:
 *   - AccountDetailPage (partyType="customer", 3 slots)
 *   - VedaTransporterFormPage (partyType="transporter", 5 slots)
 */
import { useState, useEffect, useRef } from 'react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';
import { FileCheck2, Upload, Trash2, Loader2 } from 'lucide-react';

export type KycDocSlot = { docType: string; label: string };

type KycDoc = { id: string; docType: string; fileRef: string; uploadedAt: string };

// ── Per-slot uploader ─────────────────────────────────────────────────────────
function KycSlot({
  docType,
  label,
  doc,
  partyType,
  partyId,
  onRefresh,
}: {
  docType: string;
  label: string;
  doc: KycDoc | undefined;
  partyType: string;
  partyId: string;
  onRefresh: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('docType', docType);
      await api.post(`/veda-agro/party-kyc/${partyType}/${partyId}/documents`, fd);
      onRefresh();
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function handleRemove() {
    if (!doc) return;
    setRemoving(true);
    try {
      await api.delete(`/veda-agro/party-kyc/${partyType}/${partyId}/documents/${doc.id}`);
      onRefresh();
    } finally {
      setRemoving(false);
    }
  }

  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center gap-3">
        <FileCheck2 size={18} className={doc ? 'text-green-600' : 'text-gray-300'} />
        <div>
          <p className="text-sm font-medium text-gray-900">{label}</p>
          {doc ? (
            <div className="mt-0.5 flex items-center gap-2">
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">On file</span>
              <span className="text-xs text-gray-400">{formatDate(doc.uploadedAt)}</span>
            </div>
          ) : (
            <p className="mt-0.5 text-xs text-gray-400">Not uploaded</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {doc ? (
          <button
            onClick={handleRemove}
            disabled={removing}
            aria-label="Remove"
            className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            {removing ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            Remove
          </button>
        ) : (
          <>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,image/*"
              className="hidden"
              onChange={handleFileChange}
            />
            <button
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs text-gray-600 hover:border-[var(--ink-0)] hover:text-[var(--ink-0)] disabled:opacity-50"
            >
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Public section component ──────────────────────────────────────────────────
export function PartyKycDocsSection({
  partyType,
  partyId,
  slots,
  title = 'KYC Documents',
}: {
  partyType: 'customer' | 'transporter';
  partyId: string;
  slots: KycDocSlot[];
  title?: string;
}) {
  const [docs, setDocs] = useState<KycDoc[]>([]);
  const [loading, setLoading] = useState(true);

  function fetchDocs() {
    setLoading(true);
    api.get(`/veda-agro/party-kyc/${partyType}/${partyId}/documents`)
      .then(r => setDocs(r.data.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  useEffect(() => { fetchDocs(); }, [partyType, partyId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--saffron-600)]" />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
      <h3 className="mb-4 font-semibold text-gray-900">{title}</h3>
      <div className="space-y-3">
        {slots.map(({ docType, label }) => (
          <KycSlot
            key={docType}
            docType={docType}
            label={label}
            doc={docs.find(d => d.docType === docType)}
            partyType={partyType}
            partyId={partyId}
            onRefresh={fetchDocs}
          />
        ))}
      </div>
    </div>
  );
}
